'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ReferenceChrome, Container, SectionLabel, SectionRule } from '@/components/pages/reference-landing/ReferenceChrome';
import { NavbarRef } from '@/components/pages/reference-landing/sections/NavbarRef';
import { FooterRef } from '@/components/pages/reference-landing/sections/FooterRef';

function CheckIcon() {
  return (
    <svg className="mt-0.5 size-4 shrink-0 text-[#129990]" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IndustrySolutionsContent() {
  const router = useRouter();

  const industries = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
        </svg>
      ),
      title: 'For B2B SaaS',
      desc: 'Win competitor alternative searches that drive high-intent demo sign-ups. RankGrip\'s keyword scanner identifies keyword gaps your competitors currently rank for and auto-publishes optimized blog content.',
      benefits: [
        'Track competitor keyword gaps on Google Search',
        'One-Click autopilot content writing & auto-publishing',
        'Monitor which keywords bring organic traffic to competitors',
        'Track Google rank improvements daily (Pro)',
      ],
      metrics: [
        { value: '+380%', label: 'Organic Impressions' },
        { value: '+245%', label: 'Demo Requests' },
        { value: '84+', label: 'Page 1 Keywords' },
        { value: '+82%', label: 'Traffic growth (30 days)' },
      ],
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      title: 'For Agencies',
      desc: 'Scale profitable blog writing services without per-seat fees killing margins. Unlimited team seats, white-label reports (Pro), and automated SEO content delivery make scaling simple.',
      benefits: [
        'Unlimited seats: Add 10 team members at $0 extra cost',
        'White-label reports with your branding (Pro)',
        'Dashboard shows clients exactly what content is scheduled',
        'Detailed keyword rankings prove your value (Pro)',
      ],
      metrics: [
        { value: '$0', label: 'Per Seat Cost' },
        { value: '3×', label: 'Faster Delivery' },
        { value: '+125%', label: 'Client Retention' },
        { value: '15 min', label: 'Setup Per Client' },
      ],
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 01-8 0" />
        </svg>
      ),
      title: 'For E-commerce',
      desc: 'Scale blog content targeting high-intent long-tail keywords that drive purchase intent. Connect your store and let RankGrip auto-publish comparison and guide posts.',
      benefits: [
        'Track \'best [product] for [use case]\' keyword search volumes',
        'Generate SEO blog posts with direct product links',
        'Schema suggestions for rich product snippets on Google',
        'Track organic traffic click-through rates (Pro)',
      ],
      metrics: [
        { value: '+520%', label: 'Organic Product Impressions' },
        { value: '+195%', label: 'Organic Blog Traffic' },
        { value: '+28%', label: 'Blog Page Views (90d)' },
        { value: '+78%', label: 'Conversion Rate' },
      ],
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
        </svg>
      ),
      title: 'For Local Services',
      desc: 'Dominate \'best [service] in [city]\' search results. Monitor competitor keywords, track location-specific keyword gaps, and deploy Local Schema markup.',
      benefits: [
        'Track location-specific keyword gaps by city',
        'Competitor keyword analysis for local services',
        'Local Schema suggestions (NAP, hours, service area)',
        'Auto-publishing localized landing page content (Pro)',
      ],
      metrics: [
        { value: '+680%', label: 'Local Search Impressions' },
        { value: '+420%', label: 'Direction Requests' },
        { value: '+310%', label: 'Phone Inquiries' },
        { value: '4.8★', label: 'AI Sentiment Score' },
      ],
    },
  ];

  const engines = [
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      ),
      title: 'Competitor Keyword Gap Scanner',
      desc: 'Scan competitor websites in real-time to find keywords they rank for but you don\'t. Get immediate winnability recommendations for each gap.',
    },
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      ),
      title: 'Autopilot Content Writer',
      desc: 'Generate fully-formatted, research-backed blog posts with relevant images, header structures, and internal links automatically.',
    },
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
        </svg>
      ),
      title: 'CMS Auto-Publisher',
      desc: 'Sync RankGrip directly with WordPress, Webflow, or Shopify to schedule and publish content automatically without leaving our platform.',
      badge: 'Pro',
    },
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
      title: 'Google Indexer & Rank Monitor',
      desc: 'Auto-submit new posts directly to Google\'s indexing engine and monitor keyword ranking changes in real-time to track compounding traffic.',
      badge: 'Pro',
    },
  ];

  return (
    <ReferenceChrome>
      <NavbarRef />

      {/* ── Hero ── */}
      <section className="relative w-full pt-14 pb-0 sm:pt-16">
        
        <Container className="relative z-10 text-center">
          <h1 className="mx-auto mt-14 max-w-3xl text-[2.5rem] font-normal leading-[1.15] tracking-tight text-gray-900 sm:text-5xl md:text-[3.5rem]">
            <span className="block">Autopilot SEO</span>
            <span className="block text-[#129990]">For Your Industry</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base font-light leading-relaxed text-gray-500 sm:text-lg">
            Every industry has unique SEO challenges. See how RankGrip&apos;s autopilot platform solves your specific pain points—from SaaS keyword acquisition to agency client scaling to e-commerce content scheduling.
          </p>
        </Container>
      </section>

      <main>
        {/* ── Industry Cards ── */}
        <section className="py-16 sm:py-20">
          <Container>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {industries.map((ind) => (
                <div key={ind.title} className="rounded-sm border border-gray-200 bg-white p-6 sm:p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="flex size-10 items-center justify-center rounded-sm border border-gray-200 bg-gray-50 text-[#129990]">
                      {ind.icon}
                    </span>
                    <h3 className="text-xl font-normal text-gray-900">{ind.title}</h3>
                  </div>
                  <p className="text-sm font-light leading-relaxed text-gray-500 mb-5">{ind.desc}</p>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">Key Benefits</h4>
                  <ul className="space-y-2 mb-5">
                    {ind.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2.5">
                        <CheckIcon />
                        <span className="text-sm font-light text-gray-600">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="grid grid-cols-2 gap-3">
                    {ind.metrics.map((m) => (
                      <div key={m.label} className="rounded-sm border border-gray-100 bg-gray-50 p-3 text-center">
                        <p className="text-base font-light text-[#129990]">{m.value}</p>
                        <p className="text-xs text-gray-500">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5">
                    <button
                      type="button"
                      onClick={() => router.push('/how-it-works')}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#129990] transition-colors hover:text-[#0D736F]"
                    >
                      See how it works
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        

        {/* ── CTA ── */}
        <section className="py-16 sm:py-20 bg-[#129990]">
          <Container className="text-center">
            <h2 className="text-2xl font-normal leading-tight tracking-tight text-white sm:text-[2.25rem]">
              Ready to Solve Your Industry&apos;s SEO Challenges?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-white/80">
              Join businesses using RankGrip to automate keyword gap scans, write SEO content, and publish to their CMS on autopilot. 7-day free trial, cancel anytime.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="relative flex items-center justify-center rounded-md border border-white/20 bg-white px-8 py-3 text-sm font-medium text-[#129990] shadow-[inset_0_1px_0px_rgba(255,255,255,0.4),0_0_0_1px_rgba(255,255,255,0.1),0_2px_6px_0_rgba(255,255,255,0.15)] transition-all duration-75 hover:brightness-105 active:translate-y-[2px] active:shadow-[inset_0_1px_0px_rgba(255,255,255,0.2),0_0_0_1px_rgba(255,255,255,0.1)]"
              >
                Start Free Trial
              </Link>
              <Link
                href="/how-it-works"
                className="relative flex items-center justify-center rounded-md border border-white/40 bg-transparent px-8 py-3 text-sm font-medium text-white shadow-[inset_0_1px_0px_rgba(255,255,255,0.1),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-75 hover:bg-white/10 active:translate-y-[2px] active:shadow-none"
              >
                See How It Works
              </Link>
            </div>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-6">
              {['7-day free trial', 'No credit card required', 'Cancel anytime'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm font-light text-white/80">
                  <svg className="size-4 shrink-0 text-white" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </Container>
        </section>
      </main>

      <FooterRef />
    </ReferenceChrome>
  );
}
